// kernel/entityMerge.ts
//
// The one definition of the "canonical DTO + local draft + runtime overlay" fusion.
//
// Two consumers read the same overlayed inventory (AGENTS.md §3.3.2): `withMergedInventory` in
// kernelStores.ts, which fuses the whole registry for every store read, and
// `readEntityKernelMergedState` in useEntityKernelState.ts, which fuses a single entity. They used
// to carry their own copy of every primitive below and had already drifted apart. Anything that
// changes the shape of a fused entity belongs here, so both paths move together.
//
// This module imports types only: kernelStores.ts sits low in the import graph and must not gain
// a cycle through it.
import type { InventoryEntityType, KernelEntityType, KernelHandle } from '#types/types';

// Any non-null, non-array object.
export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// A plain object literal (not a class instance), safe to merge key by key.
export const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (!isRecord(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

// Keys whose value is replaced wholesale on merge instead of being deep-merged.
export const ATOMIC_MERGE_REPLACE_KEYS = new Set(['activeSubject']);

// Applied to every value the merge emits, bar the `patch === undefined` short-circuit that hands
// `base` back untouched. The default is the identity, which is the neutral
// fusion; a caller that needs its drafts cleaned on the way out (recursively dropping the keys
// whose value is `undefined`, arrays and plain objects included) passes its own normalizer
// instead of forking the merge. The hot registry path deliberately stays on the identity, see
// `withMergedInventory`. Local on purpose: no consumer annotates it.
type MergeValueNormalizer = (value: unknown) => unknown;

const identity: MergeValueNormalizer = (value) => value;

// Recursively overlay `patch` onto `base`; atomic keys and non-objects replace wholesale.
export const deepMerge = (
  base: unknown,
  patch: unknown,
  path: string[] = [],
  normalize: MergeValueNormalizer = identity
): unknown => {
  if (patch === undefined) return base;
  const currentKey = path[path.length - 1] ?? '';
  if (isPlainObject(patch) && ATOMIC_MERGE_REPLACE_KEYS.has(currentKey)) {
    return normalize(patch);
  }
  if (isPlainObject(base) && isPlainObject(patch)) {
    const next: Record<string, unknown> = { ...base };
    Object.entries(patch).forEach(([key, value]) => {
      if (value === undefined) return;
      next[key] = deepMerge(base[key], value, [...path, key], normalize);
    });
    return normalize(next);
  }
  return normalize(patch);
};

// Flatten an entity's `runtime` overlay onto its top level so callers read one coherent entity.
const mergeRuntimeOverlayIntoEntity = (entity: unknown): unknown => {
  if (!isRecord(entity)) return entity;
  const runtime = entity.runtime;
  if (!isRecord(runtime)) return entity;
  return {
    ...entity,
    ...runtime,
  };
};

// The fused read: canonical DTO under the local draft, runtime overlay flattened on top.
export const mergeEntityWithDraft = (
  canonical: unknown,
  draft: unknown,
  normalize: MergeValueNormalizer = identity
): unknown => mergeRuntimeOverlayIntoEntity(deepMerge(canonical, draft, [], normalize));

// Maps a kernel draft entity type to its inventory slice (null = not inventory-backed).
export const KERNEL_ENTITY_TO_INVENTORY_ENTITY: Record<KernelEntityType, InventoryEntityType | null> = {
  Property: 'Property',
  Floor: 'Floor',
  Unit: 'Unit',
  UnitPosition: null,
  Portfolio: 'Portfolio',
  Brand: 'Brand',
  BrandLocation: 'BrandLocation',
  LegalEntityFinancialRecord: 'LegalEntityFinancialRecord',
  LegalEntity: 'LegalEntity',
  MarketSignal: 'MarketSignal',
  Campaign: 'Campaign',
  SourcingCampaign: 'SourcingCampaign',
  PortfolioFitCampaign: 'PortfolioFitCampaign',
  PortfolioFit: 'PortfolioFit',
  Deal: 'Deal',
  ChatSession: 'ChatSession',
  GeneratedDocument: 'GeneratedDocument',
  Lead: 'Lead',
  Project: 'Project',
  Organisation: 'Organisation',
  User: 'User',
  Notification: 'Notification',
  Contact: 'Contact',
  Audit: 'Audit',
  SlideCorrection: 'SlideCorrection',
  UniversalFact: 'UniversalFact',
  StudioWorkspace: 'StudioWorkspace',
  StudioLayer: 'StudioLayer',
  StudioWorkspaceVersion: 'StudioWorkspaceVersion',
};

// The one read of a canonical row: drafts are pruned and sent autosaves released against the same row.
export const readCanonicalEntity = (
  k: Pick<KernelHandle, 'getState'> | null,
  entityType: KernelEntityType,
  entityId: string
): Record<string, unknown> | null => {
  if (!k || !entityType || !entityId) return null;
  const inventoryEntityType = KERNEL_ENTITY_TO_INVENTORY_ENTITY[entityType];
  if (!inventoryEntityType) return null;
  const entity = k.getState().inventory[inventoryEntityType].registry[entityId];
  return isRecord(entity) ? entity : null;
};

// Draft normalization and comparison, shared by the draft store and the autosave engine.
const toComparablePrimitive = (value: unknown): unknown => {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value;
  if (typeof value !== 'object') return value;
  if (isPlainObject(value)) return value;

  const withToNumber = value as { toNumber?: () => unknown };
  if (typeof withToNumber.toNumber === 'function') {
    const numeric = withToNumber.toNumber();
    if (typeof numeric === 'number' && Number.isFinite(numeric)) {
      return numeric;
    }
  }

  const withToJSON = value as { toJSON?: () => unknown };
  if (typeof withToJSON.toJSON === 'function') {
    const json = withToJSON.toJSON();
    if (
      json === null ||
      typeof json === 'string' ||
      typeof json === 'number' ||
      typeof json === 'boolean'
    ) {
      return json;
    }
  }

  const withToString = value as { toString?: () => string };
  if (typeof withToString.toString === 'function' && withToString.toString !== Object.prototype.toString) {
    const text = withToString.toString();
    if (typeof text === 'string' && text !== '[object Object]') {
      return text;
    }
  }

  return value;
};

export const normalizeDraftValue = (value: unknown): unknown => {
  if (value === undefined) return undefined;

  if (Array.isArray(value)) {
    return value.map((entry) => normalizeDraftValue(entry));
  }

  if (isPlainObject(value)) {
    return Object.entries(value).reduce<Record<string, unknown>>((acc, [key, entry]) => {
      const normalized = normalizeDraftValue(entry);
      if (normalized !== undefined) acc[key] = normalized;
      return acc;
    }, {});
  }

  return value;
};

export const cleanRecord = (value: Record<string, unknown>): Record<string, unknown> =>
  Object.entries(value).reduce<Record<string, unknown>>((acc, [key, entry]) => {
    const normalized = normalizeDraftValue(entry);
    if (normalized !== undefined) acc[key] = normalized;
    return acc;
  }, {});

export const deepEqual = (left: unknown, right: unknown): boolean => {
  const normalizedLeft = toComparablePrimitive(left);
  const normalizedRight = toComparablePrimitive(right);
  if (Object.is(normalizedLeft, normalizedRight)) return true;

  left = normalizedLeft;
  right = normalizedRight;

  if (Object.is(left, right)) return true;

  if (Array.isArray(left) && Array.isArray(right)) {
    if (left.length !== right.length) return false;
    for (let i = 0; i < left.length; i += 1) {
      if (!deepEqual(left[i], right[i])) return false;
    }
    return true;
  }

  if (isPlainObject(left) && isPlainObject(right)) {
    const leftKeys = Object.keys(left);
    const rightKeys = Object.keys(right);
    if (leftKeys.length !== rightKeys.length) return false;
    for (const key of leftKeys) {
      if (!Object.prototype.hasOwnProperty.call(right, key)) return false;
      if (!deepEqual(left[key], right[key])) return false;
    }
    return true;
  }

  return false;
};
